import React, { useEffect, useState } from "react";

// Module-level toast API: usable from anywhere (components, services, axios
// interceptors) without context plumbing. <ToastHost /> renders the queue.
let pushToast = null;
let nextId = 1;

function emit(message, variant, duration, action) {
  if (!message) return;
  if (pushToast) {
    pushToast({
      id: nextId++,
      message: String(message),
      variant,
      duration,
      action,
    });
  } else {
    // Host not mounted (e.g. very early in boot) — don't lose the signal.
    // eslint-disable-next-line no-console
    console[variant === "error" ? "error" : "log"]("[toast]", message);
  }
}

// Second arg is either a duration in ms or {duration, action}, where action
// is {label, url} rendered as a link that opens in a new tab.
function parseOpts(opts, defaultDuration) {
  if (typeof opts === "number") return { duration: opts };
  return { duration: defaultDuration, ...opts };
}

export const toast = {
  success: (msg, opts) => {
    const { duration, action } = parseOpts(opts, 3500);
    emit(msg, "success", duration, action);
  },
  error: (msg, opts) => {
    const { duration, action } = parseOpts(opts, 6000);
    emit(msg, "error", duration, action);
  },
  warning: (msg, opts) => {
    const { duration, action } = parseOpts(opts, 5000);
    emit(msg, "warning", duration, action);
  },
  info: (msg, opts) => {
    const { duration, action } = parseOpts(opts, 3500);
    emit(msg, "info", duration, action);
  },
};

export function ToastHost() {
  const [toasts, setToasts] = useState([]);

  useEffect(() => {
    pushToast = (t) => {
      setToasts((prev) => [...prev.slice(-4), t]);
      if (t.duration > 0) {
        setTimeout(() => {
          setToasts((prev) => prev.filter((x) => x.id !== t.id));
        }, t.duration);
      }
    };
    return () => {
      pushToast = null;
    };
  }, []);

  if (toasts.length === 0) return null;

  return (
    <div className="toast-host" role="status" aria-live="polite">
      {toasts.map((t) => (
        <div
          key={t.id}
          className={`toast toast-${t.variant}`}
          onClick={() =>
            setToasts((prev) => prev.filter((x) => x.id !== t.id))
          }
        >
          {t.message}
          {t.action?.url && (
            <a
              href={t.action.url}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => e.stopPropagation()}
              style={{
                marginLeft: "8px",
                color: "inherit",
                fontWeight: 600,
                textDecoration: "underline",
              }}
            >
              {t.action.label || "View"} →
            </a>
          )}
        </div>
      ))}
    </div>
  );
}

export default ToastHost;
